import { AdvHeading, AdvPanel, AdvSection, AdvUrgent, Btn, ContactForm, type FormField, useText } from '@kkf/design'
import { GARAGE_BAND, GARAGE_CONTACT_HEADING, GARAGE_CONTACT_INFOS } from '../data/garage'
import { useT, type MessageKey } from '../i18n'
import { TURNSTILE_SITE_KEY } from '../data/links'

/**
 * The contact page, in the dark register like the rest of the app.
 *
 * `reason` is the field that matters: the three people who write to this workshop want
 * completely different things — a recovery, a placement for a young person, or a look
 * around — and one of them is urgent. Asking which up front is what routes the message to
 * somebody who can answer it, and it is why the recovery band sits above the form rather
 * than below it.
 *
 * **`WorkshopBand` is gone.** It was this app's own pale version of the same block, and
 * `AdvUrgent` in the design system now does the job on all three pages that need it — one
 * definition instead of two, which is what ground rule 5 asks for.
 */
/**
 * The fields, as keys.
 *
 * Module-level so the array is not rebuilt per render, which means it cannot call `t` —
 * hence keys here and the lookup in the component. `name` is the field's identity and is
 * never translated: it is what a submission is keyed on.
 */
const FIELDS: { name: string; placeholderKey: MessageKey; type?: FormField['type']; colClassName?: string }[] = [
  { name: 'name', placeholderKey: 'form.name' },
  { name: 'email', placeholderKey: 'form.email', type: 'email' },
  { name: 'phone', placeholderKey: 'form.phone', type: 'tel' },
  { name: 'reason', placeholderKey: 'form.kind' },
  { name: 'message', placeholderKey: 'form.message', type: 'textarea', colClassName: 'col-12' },
]

export function ContactPage() {
  const t = useT()
  const fields: FormField[] = FIELDS.map(({ placeholderKey, ...field }) => ({
    ...field,
    placeholder: t(placeholderKey),
  }))
  const s = useText()
  return (
    <>
      <AdvSection glow="tr" className="ul-adv-page-top" containerClassName="wow animate__fadeInUp">
        {/* This page's own name — the one <h1> on it. The garage's inner pages carry no
            breadcrumb banner, so the opening heading is the page heading. */}
        <AdvHeading
          eyebrow={s(GARAGE_CONTACT_HEADING.subTitle)}
          title={t('contact.heading')}
          titleAccent={t('contact.headingAccent')}
          lead={s(GARAGE_CONTACT_HEADING.descr)}
          as="h1"
        />
      </AdvSection>

      {/* OFF THE ROAD? — the signal glow, once per page, and above the form because
          anybody in the first group should not be filling in five fields. */}
      <AdvSection glow="signal" tight edged flush containerClassName="wow animate__fadeInUp">
        <AdvUrgent
          eyebrow={s(GARAGE_BAND.subTitle)}
          title={t('band.recovery')}
          titleAccent={t('band.recoveryAccent')}
          note={s(GARAGE_BAND.note)}
        >
          <Btn href="#reach-us" className="ul-btn--signal">
            {s(GARAGE_BAND.ctaLabel)}
          </Btn>
        </AdvUrgent>
      </AdvSection>

      <AdvSection glow="bl" id="reach-us" className="ul-product-anchor" containerClassName="wow animate__fadeInUp">
        <div className="row row-cols-md-2 row-cols-1 gy-4 ul-bs-row align-items-start">
          <div className="col">
            <AdvHeading eyebrow={t('contact.whereEyebrow')} title={t('contact.reach')} titleAccent={t('contact.reachAccent')} size="sm" as="h2" />
            <div className="ul-adv-panel">
              {GARAGE_CONTACT_INFOS.map((info) => (
                <div className="ul-addon-row" key={s(info.title)}>
                  <h3 className="ul-addon-row-name">
                    {s(info.title)}
                    <span className="ul-addon-row-note">{s(info.value)}</span>
                  </h3>
                  <span className="ul-addon-row-price">
                    {info.href ? (
                      <a href={info.href}>{s(info.action)}</a>
                    ) : (
                      s(info.action)
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="col">
            <AdvPanel>
              <AdvHeading eyebrow={t('contact.sendEyebrow')} title={t('contact.tellUs')} titleAccent={t('contact.tellUsAccent')} size="sm" as="h2" />
              {/* Both anti-spam measures come with the block — see `ContactForm`. The key is
                  unset today, so no widget renders and the form looks exactly as it did. */}
              <ContactForm
                id="garage-contact"
                fields={fields}
                submitLabel={s(GARAGE_CONTACT_HEADING.submitLabel)}
                turnstileSiteKey={TURNSTILE_SITE_KEY}
              />
            </AdvPanel>
          </div>
        </div>
      </AdvSection>
    </>
  )
}
